import { Instagram, Facebook, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HOME } from '@/constants/testIds';
import BrandLogo from '@/components/BrandLogo';

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] relative overflow-hidden">
      <div className="absolute inset-0 grain opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="shrink-0">
                <BrandLogo size={72} />
              </span>
              <div className="leading-tight">
                <div className="font-display text-xl">EcoBike Camping Supplies Co.</div>
                <div className="text-[10px] uppercase tracking-[0.28em] opacity-75">
                  Heritage Estate Outfitter · Since the 1920s
                </div>
              </div>
            </div>
            <p className="mt-5 text-sm opacity-80 leading-relaxed max-w-md">
              A gamekeeper&rsquo;s heirloom that stayed in the family, and a working life on the
              hill. Gear that survives the Highlands, chosen by a Gamekeeper for anyone who
              respects the ground they&rsquo;re riding on. The estate mark stands for
              ruggedness and respect &mdash; nothing less goes on the site.
            </p>
          </div>

          <div className="md:col-span-3">
            <div className="font-eyebrow !text-[var(--brand-gold)]">Explore</div>
            <ul className="mt-4 space-y-2 text-sm">
              {[
                ['ebikes', 'E-Bikes'],
                ['camping', 'Camping Supplies'],
                ['adaptive', 'Adaptive Cycling'],
                ['field-notes', 'Field Notes'],
                ['about', 'Our Story'],
              ].map(([id, label]) => (
                <li key={id}>
                  <button
                    onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })}
                    className="link-expand opacity-85 hover:opacity-100"
                  >
                    {label}
                  </button>
                </li>
              ))}
              <li>
                <Link
                  to="/heritage-estate"
                  data-testid="footer-heritage-estate-link"
                  className="link-expand opacity-85 hover:opacity-100"
                >
                  Heritage Estate
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-contact-link"
                  className="link-expand opacity-85 hover:opacity-100"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/campsites"
                  data-testid="footer-campsites-link"
                  className="link-expand opacity-85 hover:opacity-100"
                >
                  Campsites
                </Link>
              </li>
              <li>
                <Link
                  to="/safety"
                  data-testid="footer-safety-link"
                  className="link-expand opacity-85 hover:opacity-100"
                >
                  Safety &amp; Emergency
                </Link>
              </li>
              <li>
                <Link
                  to="/guides"
                  data-testid="footer-guides-link"
                  className="link-expand opacity-85 hover:opacity-100"
                >
                  Coming Home Guide
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-eyebrow !text-[var(--brand-gold)]">Get in touch</div>
            <p className="mt-4 text-sm opacity-85 leading-relaxed">
              Online only — based on the west coast.<br />
              Ayrshire, Scotland · United Kingdom
            </p>
            <p className="mt-3 text-xs opacity-70 leading-relaxed">
              Calls answered by our reception service.<br />
              Drop us a note via the enquiry form and we’ll be in touch within one working day.
            </p>
            <div className="mt-5 flex items-center gap-4">
              <a
                href="mailto:hello@ecobikecampingsuppliesco.com"
                aria-label="Email"
                className="w-9 h-9 grid place-items-center border border-[var(--brand-parchment)]/40 hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] transition-colors"
              >
                <Mail size={16} />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                className="w-9 h-9 grid place-items-center border border-[var(--brand-parchment)]/40 hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href="#"
                aria-label="Facebook"
                className="w-9 h-9 grid place-items-center border border-[var(--brand-parchment)]/40 hover:border-[var(--brand-gold)] hover:text-[var(--brand-gold)] transition-colors"
              >
                <Facebook size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="hairline mt-14 opacity-30" />
        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs opacity-70">
          <p>
            © <span data-testid={HOME.footerYear}>{year}</span>{' '}
            EcoBike Camping Supplies Co. All rights reserved.{' '}
            <span className="opacity-60">A Gamekeeper-run Scottish business &middot; Sole trader registered in Scotland.</span>
          </p>
          <nav className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <Link to="/terms" data-testid="footer-terms-link" className="link-expand">Terms of Service</Link>
            <span className="opacity-40">·</span>
            <Link to="/conditions-of-sale" data-testid="footer-conditions-link" className="link-expand">Conditions of Sale</Link>
            <span className="opacity-40">·</span>
            <Link to="/shipping" data-testid="footer-shipping-link" className="link-expand">Shipping</Link>
            <span className="opacity-40">·</span>
            <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="link-expand">Disclaimer</Link>
            <span className="opacity-40">·</span>
            <Link to="/privacy" data-testid="footer-privacy-link" className="link-expand">Privacy</Link>
            <span className="opacity-40">·</span>
            <Link to="/cookies" data-testid="footer-cookies-link" className="link-expand">Cookies</Link>
            <span className="opacity-40">·</span>
            <Link to="/compliance" data-testid="footer-compliance-link" className="link-expand">Compliance</Link>
            <span className="opacity-40">·</span>
            <Link to="/the-bothy" data-testid="footer-bothy-login-link" className="link-expand">Bothy Login</Link>
          </nav>
          <p>
            ecobikecampingsuppliesco.com · ecobikecampingsuppliesco.co.uk
          </p>
        </div>
      </div>
    </footer>
  );
}
